import {
  VehicleInfoLookup,
  VehicleModelTrimLookup,
  VehicleType,
  VinWarrantyExpiry
} from '../components/common/CommonTypes';
import { ApiHelper } from './apiHelper';

/**
 * Client-side API wrapper for vehicle lookup endpoints.
 */
export class VehicleApi {
  /**
   * Retrieves vehicle details by VRN.
   *
   * @param vrn Vehicle registration number.
   * @param token Bearer token used for authenticated requests.
   * @param successCallback Invoked with vehicle details on success.
   * @param failureCallback Invoked when the request fails.
   * @param signal Abort signal used to cancel the request.
   */
  async getRegisteredVehicles(
    vrn: string,
    token: string,
    successCallback: (vehicle: VehicleType) => void,
    failureCallback: () => void,
    signal: AbortSignal
  ): Promise<void> {
    const dataUrl = `/api/vehicle/vehicle?vrn=${vrn}`;

    await new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      successCallback,
      failureCallback,
      signal
    );
  }

  /**
   * Retrieves vehicle details by VIN.
   *
   * @param vin Vehicle identification number.
   * @param token Bearer token used for authenticated requests.
   * @param successCallback Invoked with vehicle details on success.
   * @param failureCallback Invoked when the request fails.
   * @param signal Abort signal used to cancel the request.
   * @param considerRegistrationDate When true, the backend may consider registration/delivery date rules.
   */
  async getRegisteredVehiclesVin(
    vin: string,
    token: string,
    successCallback: (vehicle: VehicleType) => void,
    failureCallback: () => void,
    signal: AbortSignal,
    considerRegistrationDate: boolean = true
  ): Promise<void> {
    const dataUrl = `/api/vehicle/vehicle/vin?vin=${vin}&considerRegistrationDate=${considerRegistrationDate}`;

    await new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      successCallback,
      failureCallback,
      signal
    );
  }

  /**
   * Loads the available vehicle types for a given country and registration date.
   */
  getVehicleTypes(
    countryCode: string | undefined | null,
    firstRegistrationDate: string | undefined | null,
    token: string,
    successCallback: (vehicleTypes: VehicleInfoLookup[]) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/vehicle/vehicleTypes/${countryCode}/${firstRegistrationDate}`;
    new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      successCallback,
      failureCallback,
      signal
    );
  }

  /**
   * Loads vehicle makes for a given registration date and vehicle type.
   */
  getVehicleMakes(
    firstRegistrationDate: string | undefined | null,
    vehicleTypeId: string,
    token: string,
    successCallback: (vehicleMakes: VehicleInfoLookup[]) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/vehicle/vehicle/${firstRegistrationDate}/${vehicleTypeId}`;
    new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      successCallback,
      failureCallback,
      signal
    );
  }

  /**
   * Loads vehicle models for a given registration date, vehicle type, and make.
   */
  getVehicleModels(
    firstRegistrationDate: string | undefined | null,
    vehicleTypeId: string,
    vehicleMakeId: string,
    token: string,
    successCallback: (vehicleModels: VehicleInfoLookup[]) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/vehicle/vehicle/${firstRegistrationDate}/${vehicleTypeId}/${vehicleMakeId}`;
    new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      successCallback,
      failureCallback,
      signal
    );
  }

  /**
   * Loads fuel types for a given vehicle selection.
   */
  getFuelTypes(
    countryCode: string | undefined | null,
    firstRegistrationDate: string | undefined | null,
    vehicleTypeId: string,
    vehicleMakeId: string,
    vehicleModelId: string,
    token: string,
    successCallback: (vehicleTypes: VehicleInfoLookup[]) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/vehicle/vehicleFuelTypes/${countryCode}/${firstRegistrationDate}/${vehicleTypeId}/${vehicleMakeId}/${vehicleModelId}`;
    new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      successCallback,
      failureCallback,
      signal
    );
  }

  /**
   * Loads engine sizes for a given vehicle selection.
   */
  getEngineSizes(
    firstRegistrationDate: string | undefined | null,
    vehicleTypeId: string,
    vehicleMakeId: string,
    vehicleModelId: string,
    fuelTypeId: string,
    token: string,
    successCallback: (vehicleTypes: VehicleInfoLookup[]) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/vehicle/vehicle/${firstRegistrationDate}/${vehicleTypeId}/${vehicleMakeId}/${vehicleModelId}/${fuelTypeId}`;
    new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      successCallback,
      failureCallback,
      signal
    );
  }

  /**
   * Loads model trims for a given vehicle selection.
   */
  getModelTrims(
    firstRegistrationDate: string | undefined | null,
    vehicleTypeId: string,
    vehicleMakeId: string,
    vehicleModelId: string,
    fuelTypeId: string,
    engineSize: string,
    token: string,
    successCallback: (vehicleTypes: VehicleModelTrimLookup[]) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/vehicle/vehicle/${firstRegistrationDate}/${vehicleTypeId}/${vehicleMakeId}/${vehicleModelId}/${fuelTypeId}/${engineSize}`;

    new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      successCallback,
      failureCallback,
      signal
    );
  }

  /**
   * Retrieves manufacturer warranty expiry information for a VIN (when available).
   */
  getVinWarrantyExpiry(
    vin: string,
    token: string,
    successCallback: (response: VinWarrantyExpiry) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/vehicle/vehicle/vinwarrantyexpiry?vin=${vin}`;
    new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      successCallback,
      failureCallback,
      signal
    );
  }
}
